import { useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials, brandStats } from '@/mocks/about';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "吃瓜网",
    "url": "http://lrayneca.cn/",
    "description": "吃瓜网是国内领先的娱乐资讯聚合平台，提供最新最全的明星塌房、网红黑料、反差视频等娱乐圈八卦资讯。",
    "email": "tips@lrayneca.cn",
    "sameAs": [
      "http://lrayneca.cn/about#weibo",
      "http://lrayneca.cn/about#wechat"
    ]
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "关于吃瓜网",
    "description": "吃瓜网成立于2024年，是国内领先的娱乐资讯聚合平台。致力于为吃瓜群众提供最新、最全、最独家的娱乐圈八卦资讯。",
    "url": "http://lrayneca.cn/about",
    "isPartOf": {
      "@type": "WebSite",
      "name": "吃瓜网",
      "url": "http://lrayneca.cn/"
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('submitting');
    try {
      const formData = new FormData(form);
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          urlEncoded.append(key, value as string);
        }
      });
      await fetch('https://readdy.ai/api/form/d90kkia181e9e92u94jg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      setFormStatus('success');
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <Header />

      <main className="pt-16">
        {/* Hero */}
        <section className="relative w-full h-[360px] md:h-[460px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20geometric%20composition%20with%20soft%20golden%20and%20cream%20shapes%20on%20light%20background%2C%20minimal%20modern%20design%2C%20elegant%20gradients%2C%20calm%20atmosphere%2C%20professional%20aesthetic%2C%20subtle%20texture%2C%20art%20deco%20inspired%20pattern%2C%20clean%20lines&width=1600&height=900&seq=about-hero&orientation=landscape"
              alt="关于吃瓜网"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/40"></div>
          </div>

          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6">
            <span className="text-xs font-semibold text-background-50/80 uppercase tracking-widest mb-3">
              About Us
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-background-50 text-center font-heading leading-tight">
              关于吃瓜网
            </h1>
            <p className="mt-3 text-sm md:text-base text-background-50/70 text-center max-w-xl">
              全网最全最及时的吃瓜平台，做最懂你的瓜田
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 md:gap-14 items-center">
              <div className="lg:w-1/2">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimal%20office%20workspace%20with%20multiple%20monitors%20displaying%20news%20feeds%2C%20warm%20ambient%20lighting%2C%20creative%20team%20environment%2C%20clean%20aesthetic%2C%20professional%20editorial%20office%2C%20soft%20golden%20hour%20light%20through%20large%20windows%2C%20collaborative%20atmosphere&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="吃瓜网团队工作环境"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="lg:w-1/2">
                <p className="text-xs font-semibold text-accent-600 uppercase tracking-widest mb-2 font-heading">
                  Our Story
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-6">
                  全网吃瓜第一站
                </h2>
                <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                  <p>
                    吃瓜网成立于2024年，是国内领先的娱乐资讯聚合平台。我们致力于为吃瓜群众提供最新、最全、最独家的娱乐圈八卦资讯。从明星塌房到网红黑料，从反差视频到聊天记录，吃瓜网始终站在吃瓜第一线。
                  </p>
                  <p>
                    我们的编辑团队由资深娱乐记者和前狗仔队成员组成，拥有丰富的行业资源和敏锐的新闻嗅觉。7×24小时全天候追踪监控各大社交平台和渠道，确保每一条猛料都能在第一时间送达你面前。
                  </p>
                  <p>
                    吃瓜网的使命是：让每一个爱吃瓜的人，都能第一时间吃到最新鲜、最劲爆的瓜。我们不生产瓜，我们只是瓜田的搬运工。
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
                  {brandStats.map((stat) => (
                    <div key={stat.label} className="text-center p-4 rounded-xl bg-background-100">
                      <p className="text-xl md:text-2xl font-bold text-accent-600 font-heading">
                        {stat.value}
                      </p>
                      <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <p className="text-xs font-semibold text-accent-600 uppercase tracking-widest mb-2 font-heading">
                Testimonials
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                吃瓜群众口碑评价
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {testimonials.map((t) => (
                <div key={t.id} className="relative rounded-2xl bg-foreground-900 p-6 md:p-8 pt-12">
                  <div className="absolute top-0 left-6 -translate-y-1/2">
                    <div className="w-16 h-16 rounded-xl overflow-hidden border-4 border-background-100">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  <div className="text-accent-400/30 text-5xl font-serif mb-3 leading-none">&ldquo;</div>
                  <p className="text-sm md:text-[15px] text-background-50/90 leading-relaxed mb-6">
                    {t.quote}
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-background-50">{t.name}</p>
                    <p className="text-xs text-background-50/50">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA + Form */}
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20abstract%20background%20with%20warm%20amber%20and%20gold%20gradient%20waves%2C%20modern%20minimal%20design%2C%20cinematic%20atmosphere%2C%20luxurious%20dark%20texture%2C%20elegant%20flowing%20lines%2C%20professional%20corporate%20style%2C%20clean%20geometric%20pattern&width=1600&height=700&seq=about-cta&orientation=landscape"
              alt="联系我们"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-foreground-950/70"></div>
          </div>

          <div className="relative z-10 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-10 md:gap-14 items-start">
                <div className="lg:w-1/2">
                  <h2 className="text-2xl md:text-4xl font-bold text-background-50 font-heading mb-4">
                    联系我们
                  </h2>
                  <p className="text-background-50/70 text-sm md:text-base leading-relaxed mb-8 max-w-md">
                    有猛料想爆料？有合作想洽谈？或者只是想和我们聊聊天？
                    无论什么事，都欢迎通过下方表单联系我们。
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50/10">
                        <i className="ri-mail-line text-background-50/80 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-background-50/50">邮箱</p>
                        <p className="text-sm text-background-50/90">tips@lrayneca.cn</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50/10">
                        <i className="ri-wechat-line text-background-50/80 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-background-50/50">微信</p>
                        <p className="text-sm text-background-50/90">lrayneca_official</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:w-1/2 w-full">
                  <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                    <h3 className="text-lg font-bold text-foreground-900 font-heading mb-6">
                      给我们留言
                    </h3>

                    <form onSubmit={handleSubmit} data-readdy-form="">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="name" className="block text-xs font-medium text-foreground-600 mb-1.5">
                              姓名 <span className="text-accent-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              required
                              className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all text-foreground-900"
                              placeholder="你的名字"
                            />
                          </div>
                          <div>
                            <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                              邮箱 <span className="text-accent-500">*</span>
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              required
                              className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all text-foreground-900"
                              placeholder="your@email.com"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="subject" className="block text-xs font-medium text-foreground-600 mb-1.5">
                            主题
                          </label>
                          <select
                            id="subject"
                            name="subject"
                            className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all text-foreground-900 cursor-pointer"
                          >
                            <option value="">请选择主题</option>
                            <option value="爆料投稿">爆料投稿</option>
                            <option value="商业合作">商业合作</option>
                            <option value="内容反馈">内容反馈</option>
                            <option value="其他问题">其他问题</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="message" className="block text-xs font-medium text-foreground-600 mb-1.5">
                            留言内容 <span className="text-accent-500">*</span>
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all text-foreground-900 resize-none"
                            placeholder="请输入你的留言内容（500字以内）"
                          ></textarea>
                          <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                        </div>

                        <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
                          <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" />
                        </div>

                        <button
                          type="submit"
                          disabled={formStatus === 'submitting'}
                          className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-medium text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer"
                        >
                          {formStatus === 'submitting' ? (
                            <span className="flex items-center justify-center gap-2">
                              <i className="ri-loader-4-line animate-spin"></i>
                              发送中...
                            </span>
                          ) : (
                            '发送留言'
                          )}
                        </button>

                        {formStatus === 'success' && (
                          <div className="mt-3 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm text-center">
                            <i className="ri-check-line mr-1"></i>
                            留言已成功发送，我们会尽快回复！
                          </div>
                        )}

                        {formStatus === 'error' && (
                          <div className="mt-3 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm text-center">
                            发送失败，请稍后重试。
                          </div>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
